/**
 * OÙ RAMENER QUELQU'UN APRÈS SA CONNEXION AU BACK-OFFICE.
 *
 * Une session qui expire dépose la page quittée dans `?redirect=` pour y
 * ramener l'utilisateur plutôt que de le lâcher sur l'accueil. Ce paramètre
 * vient de la barre d'adresse : il est écrit par n'importe qui, et se lit donc
 * comme une saisie hostile.
 *
 * SEUL UN CHEMIN INTERNE EST ACCEPTÉ. `//ailleurs.example` est une URL absolue
 * pour le navigateur, et un `redirect` fabriqué enverrait la victime hors du
 * back-office juste après qu'elle a tapé son mot de passe.
 *
 * LE FILTRE ÉCRIT « À LA MAIN » NE SUFFISAIT PAS. Il refusait `//` en tête,
 * mais le navigateur normalise l'antislash et les caractères de contrôle :
 * `/\example.com` et `/<tab>/example.com` passaient le contrôle puis
 * devenaient le chemin `//example.com`. D'où les trois gardes successives —
 * on refuse l'évident, puis on RÉSOUT l'adresse comme le navigateur le fera,
 * et on n'accepte que ce qui retombe sur notre propre origine.
 *
 * `resolveLoginRedirect` sert deux fois dans l'écran de connexion : pour la
 * page à rouvrir, et pour savoir s'il faut annoncer une session reprise — le
 * bandeau « nous vous ramenons sur la page que vous consultiez » ne doit pas
 * promettre un retour qui n'aura pas lieu.
 */

/** Ce qu'on ouvre quand le `redirect` ne vaut rien : l'accueil. */
export const ACCUEIL = '/'

/**
 * Antislash et caractères de contrôle (tabulation, saut de ligne, NUL…) : le
 * navigateur les efface ou les convertit en séparateur de chemin. Aucun chemin
 * légitime du back-office n'en contient.
 */
const CARACTERES_INTERDITS = /[\\\u0000-\u001f\u007f]/

/**
 * @param {unknown} redirect - La valeur brute de `?redirect=` (une adresse
 *   répétée donne un tableau : tout ce qui n'est pas une chaîne est refusé).
 * @param {string} origine - L'origine de la page (`window.location.origin`).
 * @returns {string} Un chemin interne sûr, ou `'/'`.
 */
export function resolveLoginRedirect(redirect, origine) {
  if (typeof redirect !== 'string' || redirect === '') return ACCUEIL
  if (!redirect.startsWith('/')) return ACCUEIL
  if (CARACTERES_INTERDITS.test(redirect)) return ACCUEIL

  let base
  let cible
  try {
    base = new URL(origine)
    cible = new URL(redirect, base)
  } catch {
    return ACCUEIL
  }

  // Sortie du back-office : `//ailleurs.example` résout sur une autre origine.
  if (cible.origin !== base.origin) return ACCUEIL
  // Reste d'un chemin en `//` que la résolution aurait gardé sur place.
  if (cible.pathname.startsWith('//')) return ACCUEIL
  // Revenir sur l'écran de connexion après s'être connecté n'a pas de sens.
  if (cible.pathname.startsWith('/login')) return ACCUEIL

  return `${cible.pathname}${cible.search}${cible.hash}`
}

/**
 * LE MOTIF DE L'ARRIVÉE SUR LA CONNEXION : `?session=expiree`.
 *
 * Un `redirect` seul ne dit pas POURQUOI on est là. Depuis que la garde dépose
 * aussi la page demandée pour quelqu'un qui n'a jamais été connecté (favori,
 * lien reçu par e-mail), lire tout `redirect` comme une expiration faisait
 * annoncer « Votre session s'est fermée » à une personne qui n'en avait pas.
 * Seuls les deux endroits qui CONSTATENT une session perdue posent ce motif :
 * la réaction au 401 (`useSessionExpiryRedirect`) et la garde du back-office
 * quand l'utilisateur affiché n'a plus de session.
 */
export const MOTIF_SESSION_EXPIREE = 'expiree'

/**
 * @param {string} loginPath
 * @param {string} fullPath la page quittée
 * @returns {{path: string, query: {redirect: string, session: string}}}
 */
export function versConnexionApresExpiration(loginPath, fullPath) {
  return { path: loginPath, query: { redirect: fullPath, session: MOTIF_SESSION_EXPIREE } }
}

/** @param {Record<string, unknown>|undefined} query */
export function estArriveeSurExpiration(query) {
  return query?.session === MOTIF_SESSION_EXPIREE
}
